import { useRef, type MutableRefObject } from "react";
import { seeded } from "../noise";
import { easeInOut, easeOut, lerp, span } from "../math";
import { useCanvas, type Frame } from "./useCanvas";

/**
 * A wireframe open pit that is assembled as `progress` goes from 0 to 1:
 *
 *   0.0 - 0.2  site boundary, collars and drillhole traces
 *   0.2 - 0.4  pit benches and a blast pattern on the next pushback
 *   0.4 - 0.6  haul ramp and trucks
 *   0.6 - 0.8  crusher, conveyor and stockpile
 *   0.8 - 1.0  everything, labelled
 *
 * Geometry is generated once from a fixed seed; each frame only projects it.
 */

type P3 = readonly [number, number, number];

const TAU = Math.PI * 2;
const BONE = "228, 232, 227";
const LIME = "203, 255, 84";

const BENCHES = 9;
const BENCH_HEIGHT = 0.07;
const FACE = 0.022;
const BERM = 0.048;
const RIM = 0.8;
const STRETCH_X = 1.22;
const STRETCH_Z = 0.86;
const RING_STEPS = 96;

const wobble = (a: number) => 1 + 0.13 * Math.sin(2 * a + 1.1) + 0.07 * Math.sin(3 * a + 0.3) + 0.03 * Math.sin(5 * a + 2);
const onPit = (radius: number, a: number, y: number): P3 => [
  radius * wobble(a) * STRETCH_X * Math.cos(a),
  y,
  radius * wobble(a) * STRETCH_Z * Math.sin(a),
];

const ring = (radius: number, y: number, phase: number): P3[] =>
  Array.from({ length: RING_STEPS + 1 }, (_, step) => onPit(radius, phase + (step / RING_STEPS) * TAU, y));

interface Ring {
  points: P3[];
  crest: boolean;
  level: number;
}

const RINGS: Ring[] = [];
for (let level = 0; level < BENCHES; level++) {
  const crest = RIM - level * (FACE + BERM);
  RINGS.push({ points: ring(crest, -level * BENCH_HEIGHT, level * 0.9), crest: true, level });
  RINGS.push({ points: ring(crest - FACE, -(level + 1) * BENCH_HEIGHT, level * 0.9 + 0.4), crest: false, level });
}

const RIM_OUTLINE = ring(RIM, 0, 0);

const BOUNDARY: P3[] = [
  [-1.42, 0, -0.98],
  [0.62, 0, -1.12],
  [1.78, 0, -0.2],
  [1.86, 0, 1.22],
  [-0.2, 0, 1.3],
  [-1.5, 0, 0.72],
  [-1.42, 0, -0.98],
];

interface Hole {
  points: P3[];
  ore: boolean[];
}

const HOLES: Hole[] = (() => {
  const random = seeded(11);
  const holes: Hole[] = [];
  const inOre = (p: P3) => ((p[0] - 0.05) / 0.52) ** 2 + ((p[1] + 0.4) / 0.17) ** 2 + (p[2] / 0.36) ** 2 < 1;

  for (let row = 0; row < 5; row++) {
    for (let column = 0; column < 6; column++) {
      const x = -0.95 + column * 0.38 + (random() - 0.5) * 0.16;
      const z = -0.62 + row * 0.31 + (random() - 0.5) * 0.14;
      let azimuth = random() * TAU;
      const dip = (62 + random() * 26) * (Math.PI / 180);
      const length = 0.42 + random() * 0.36;
      const points: P3[] = [];
      for (let step = 0; step <= 14; step++) {
        const along = (step / 14) * length;
        azimuth += 0.018;
        points.push([
          x + Math.cos(azimuth) * Math.cos(dip) * along,
          -Math.sin(dip) * along,
          z + Math.sin(azimuth) * Math.cos(dip) * along,
        ]);
      }
      holes.push({ points, ore: points.map(inOre) });
    }
  }
  return holes;
})();

const PATTERN: P3[] = (() => {
  const dots: P3[] = [];
  for (let row = 0; row < 4; row++) {
    for (let hole = 0; hole < 18; hole++) {
      const a = 3.42 + ((hole + (row % 2) * 0.5) / 18) * 1.25;
      dots.push(onPit(RIM + 0.045 + row * 0.04, a, 0));
    }
  }
  return dots;
})();

const RAMP_END_ANGLE = 0.9;
const PLANT: P3 = [1.25, 0, 0.95];
const STOCKPILE: P3 = [1.64, 0, 0.66];

const RAMP: P3[] = Array.from({ length: 161 }, (_, step) => {
  const u = step / 160;
  const depth = (1 - u) * BENCHES;
  return onPit(RIM - depth * (FACE + BERM), RAMP_END_ANGLE - (1 - u) * TAU * 1.15, -depth * BENCH_HEIGHT);
});

const ROAD: P3[] = (() => {
  const from = RAMP[RAMP.length - 1];
  const control: P3 = [0.98, 0, 0.6];
  return Array.from({ length: 31 }, (_, step) => {
    const t = step / 30;
    const k = 1 - t;
    return [
      k * k * from[0] + 2 * k * t * control[0] + t * t * PLANT[0],
      0,
      k * k * from[2] + 2 * k * t * control[2] + t * t * PLANT[2],
    ] as const;
  });
})();

const HAUL = [...RAMP, ...ROAD.slice(1)];

const box = (center: P3, size: P3): P3[][] => {
  const [cx, cy, cz] = center;
  const [w, h, d] = [size[0] / 2, size[1], size[2] / 2];
  const base: P3[] = [
    [cx - w, cy, cz - d],
    [cx + w, cy, cz - d],
    [cx + w, cy, cz + d],
    [cx - w, cy, cz + d],
  ];
  const top = base.map((p) => [p[0], cy + h, p[2]] as const);
  return [[...base, base[0]], [...top, top[0]], ...base.map((p, index) => [p, top[index]])];
};

const PLANT_EDGES: P3[][] = [
  ...box(PLANT, [0.16, 0.1, 0.13]),
  ...box([PLANT[0] + 0.02, 0.1, PLANT[2]], [0.07, 0.05, 0.07]),
  ...box([1.42, 0, 1.06], [0.11, 0.06, 0.09]),
];

const STOCKPILE_APEX: P3 = [STOCKPILE[0], 0.17, STOCKPILE[2]];
const STOCKPILE_BASE: P3[] = Array.from({ length: 25 }, (_, step) => {
  const a = (step / 24) * TAU;
  return [STOCKPILE[0] + Math.cos(a) * 0.15, 0, STOCKPILE[2] + Math.sin(a) * 0.13] as const;
});
const CONVEYOR: P3[] = [[PLANT[0] + 0.06, 0.12, PLANT[2] - 0.03], [STOCKPILE[0], 0.2, STOCKPILE[2]]];

const CALLOUTS: { anchor: P3; label: string; at: number; dx: number; dy: number }[] = [
  { anchor: HOLES[20].points[0], label: "Drillholes", at: 0.1, dx: -70, dy: -64 },
  { anchor: PATTERN[40], label: "Blast pattern", at: 0.31, dx: -64, dy: -52 },
  { anchor: RAMP[112], label: "Haul road", at: 0.5, dx: 78, dy: -70 },
  { anchor: [PLANT[0], 0.15, PLANT[2]], label: "Plant", at: 0.68, dx: 56, dy: -62 },
];

interface Props {
  /** 0..1, written by the scroll trigger that owns the scene. */
  progress: MutableRefObject<number>;
  animate: boolean;
  className?: string;
}

export function PitScene({ progress, animate, className }: Props) {
  const shown = useRef(animate ? 0 : 1);

  const draw = ({ ctx, width, height, time }: Frame) => {
    // Ease toward the scroll position so the scene never snaps.
    shown.current = animate ? shown.current + (progress.current - shown.current) * 0.14 : progress.current;
    const p = shown.current;
    const clock = animate ? time : 0;

    const pullBack = easeInOut(span(p, 0.5, 0.74));
    // Ends with the plant to the right of the pit, away from the copy; sways slightly so it never sits still.
    const yaw = -1.5 + p * 1.1 + Math.sin(clock * 0.22) * 0.05;
    const pitch = 0.6;
    const cosYaw = Math.cos(yaw);
    const sinYaw = Math.sin(yaw);
    const sinPitch = Math.sin(pitch);
    const cosPitch = Math.cos(pitch);

    // On wide screens the scene sits right of centre, clear of the copy.
    const wide = width >= 900;
    const base = Math.min(width / (wide ? 3.3 : 2.9), height / 1.9);
    const zoom = base * lerp(1.02, 0.8, pullBack);
    const targetX = lerp(0, 0.42, pullBack);
    const targetZ = lerp(0, 0.24, pullBack);
    const originX = width * (wide ? 0.6 : 0.5);
    const originY = height * (wide ? 0.44 : 0.36);

    // Distance toward the viewer along the ground; larger is nearer.
    const depth = (q: P3) => (q[0] - targetX) * sinYaw + (q[2] - targetZ) * cosYaw;
    const sx = (q: P3) => originX + ((q[0] - targetX) * cosYaw - (q[2] - targetZ) * sinYaw) * zoom;
    const sy = (q: P3) => originY + (depth(q) * sinPitch - q[1] * cosPitch) * zoom;
    const pitDepth = depth([0, 0, 0]);

    const trace = (points: readonly P3[], count = points.length) => {
      ctx.beginPath();
      for (let index = 0; index < count; index++) {
        const q = points[index];
        if (index === 0) ctx.moveTo(sx(q), sy(q));
        else ctx.lineTo(sx(q), sy(q));
      }
    };
    const line = (points: readonly P3[], style: string, lineWidth = 1, count?: number) => {
      trace(points, count);
      ctx.strokeStyle = style;
      ctx.lineWidth = lineWidth;
      ctx.stroke();
    };

    ctx.clearRect(0, 0, width, height);
    ctx.lineJoin = "round";
    ctx.lineCap = "round";

    const pit = span(p, 0.2, 0.36);

    // ── Ground grid (faded out over the pit once it is excavated) ───────
    const GRID_ALPHA = 0.085;
    const drawGrid = (alpha: number) => {
      if (alpha <= 0) return;
      ctx.strokeStyle = `rgba(${BONE}, ${alpha})`;
      ctx.lineWidth = 1;
      ctx.beginPath();
      for (let step = -8; step <= 8; step++) {
        const at = step * 0.25;
        ctx.moveTo(sx([at, 0, -2]), sy([at, 0, -2]));
        ctx.lineTo(sx([at, 0, 2]), sy([at, 0, 2]));
        ctx.moveTo(sx([-2, 0, at]), sy([-2, 0, at]));
        ctx.lineTo(sx([2, 0, at]), sy([2, 0, at]));
      }
      ctx.stroke();
    };
    ctx.save();
    ctx.beginPath();
    ctx.rect(0, 0, width, height);
    for (let index = 0; index < RIM_OUTLINE.length; index++) {
      const q = RIM_OUTLINE[index];
      if (index === 0) ctx.moveTo(sx(q), sy(q));
      else ctx.lineTo(sx(q), sy(q));
    }
    ctx.closePath();
    ctx.clip("evenodd");
    drawGrid(GRID_ALPHA);
    ctx.restore();
    if (pit < 1) {
      ctx.save();
      trace(RIM_OUTLINE);
      ctx.clip();
      drawGrid(GRID_ALPHA * (1 - pit));
      ctx.restore();
    }

    // ── Site boundary ────────────────────────────────────────────────────
    const boundary = easeOut(span(p, 0, 0.09));
    if (boundary > 0) {
      ctx.setLineDash([5, 6]);
      line(BOUNDARY, `rgba(${LIME}, ${0.55 * boundary})`, 1, Math.max(2, Math.ceil(boundary * BOUNDARY.length)));
      ctx.setLineDash([]);
      ctx.fillStyle = `rgba(${LIME}, ${0.8 * boundary})`;
      for (let index = 0; index < BOUNDARY.length - 1; index++) {
        if (index / BOUNDARY.length > boundary) break;
        ctx.fillRect(sx(BOUNDARY[index]) - 2, sy(BOUNDARY[index]) - 2, 4, 4);
      }
    }

    // ── Drillholes ───────────────────────────────────────────────────────
    const holeFade = lerp(1, 0.32, span(p, 0.2, 0.34));
    HOLES.forEach((hole, index) => {
      const offset = index * 0.0035;
      const collar = easeOut(span(p, 0.025 + offset, 0.06 + offset));
      if (collar <= 0) return;
      const grown = span(p, 0.06 + offset, 0.19);
      const count = Math.max(1, Math.round(grown * (hole.points.length - 1)) + 1);

      if (count > 1) {
        line(hole.points, `rgba(${BONE}, ${0.42 * holeFade})`, 1, count);
        // Mineralised intervals.
        ctx.strokeStyle = `rgba(${LIME}, ${0.95 * holeFade})`;
        ctx.lineWidth = 2;
        ctx.beginPath();
        for (let step = 1; step < count; step++) {
          if (!hole.ore[step] || !hole.ore[step - 1]) continue;
          ctx.moveTo(sx(hole.points[step - 1]), sy(hole.points[step - 1]));
          ctx.lineTo(sx(hole.points[step]), sy(hole.points[step]));
        }
        ctx.stroke();
      }

      const c = hole.points[0];
      ctx.fillStyle = `rgba(${BONE}, ${0.9 * collar * Math.max(holeFade, 0.5)})`;
      ctx.beginPath();
      ctx.arc(sx(c), sy(c), 1.5 + 1.5 * collar, 0, TAU);
      ctx.fill();
    });

    // ── Pit benches, drawn from the rim down ─────────────────────────────
    if (pit > 0) {
      RINGS.forEach((entry, index) => {
        const drawn = easeOut(span(pit, index / (RINGS.length + 6), (index + 7) / (RINGS.length + 6)));
        if (drawn <= 0) return;
        const depthFade = 1 - (entry.level / BENCHES) * 0.45;
        const alpha = (entry.crest ? 0.7 : 0.26) * depthFade;
        const count = Math.max(2, Math.ceil(drawn * entry.points.length));
        ctx.lineWidth = entry.crest ? 1.15 : 1;

        // The far wall faces the viewer; the near wall is mostly hidden by
        // its own rim, so it is drawn fainter. That is what makes the rings
        // read as a hole rather than a hill.
        for (const near of [false, true]) {
          ctx.strokeStyle = `rgba(${BONE}, ${alpha * (near ? 0.34 : 1)})`;
          ctx.beginPath();
          let drawing = false;
          for (let step = 0; step < count; step++) {
            const q = entry.points[step];
            if (depth(q) > pitDepth !== near) {
              if (drawing) ctx.lineTo(sx(q), sy(q));
              drawing = false;
            } else if (drawing) {
              ctx.lineTo(sx(q), sy(q));
            } else {
              ctx.moveTo(sx(q), sy(q));
              drawing = true;
            }
          }
          ctx.stroke();
        }
      });
    }

    // ── Blast pattern on the next pushback ───────────────────────────────
    const pattern = span(p, 0.27, 0.39);
    if (pattern > 0) {
      const visible = Math.ceil(pattern * PATTERN.length);
      ctx.fillStyle = `rgba(${LIME}, 0.95)`;
      for (let index = 0; index < visible; index++) {
        const q = PATTERN[index];
        ctx.beginPath();
        ctx.arc(sx(q), sy(q), 1.7, 0, TAU);
        ctx.fill();
      }
    }

    // ── Haul ramp, road and trucks ───────────────────────────────────────
    const ramp = easeInOut(span(p, 0.4, 0.52));
    const road = easeInOut(span(p, 0.58, 0.66));
    if (ramp > 0) {
      line(RAMP, `rgba(${BONE}, 0.82)`, 1.5, Math.max(2, Math.ceil(ramp * RAMP.length)));
      if (road > 0) line(ROAD, `rgba(${BONE}, 0.82)`, 1.5, Math.max(2, Math.ceil(road * ROAD.length)));

      const reach = road > 0 ? RAMP.length + Math.ceil(road * (ROAD.length - 1)) : Math.ceil(ramp * RAMP.length);
      const trucks = easeOut(span(p, 0.46, 0.54));
      for (let truck = 0; truck < 6; truck++) {
        const phase = (clock * 0.03 + truck / 6) % 1;
        const there = phase < 0.5 ? phase * 2 : 2 - phase * 2;
        const q = HAUL[Math.min(reach - 1, Math.floor(there * (reach - 1)))];
        const x = sx(q);
        const y = sy(q);
        ctx.fillStyle = `rgba(${LIME}, ${0.16 * trucks})`;
        ctx.beginPath();
        ctx.arc(x, y, 8, 0, TAU);
        ctx.fill();
        ctx.fillStyle = `rgba(${LIME}, ${trucks})`;
        ctx.beginPath();
        ctx.arc(x, y, 3, 0, TAU);
        ctx.fill();
      }
    }

    // ── Crusher, conveyor and stockpile ──────────────────────────────────
    const plant = easeOut(span(p, 0.62, 0.74));
    if (plant > 0) {
      PLANT_EDGES.forEach((edge) => line(edge, `rgba(${BONE}, ${0.8 * plant})`, 1.1));
      line(STOCKPILE_BASE, `rgba(${BONE}, ${0.55 * plant})`, 1);
      for (let index = 0; index < STOCKPILE_BASE.length - 1; index += 3) {
        line([STOCKPILE_BASE[index], STOCKPILE_APEX], `rgba(${BONE}, ${0.4 * plant})`, 1);
      }
      ctx.setLineDash([6, 5]);
      ctx.lineDashOffset = -clock * 22;
      line(CONVEYOR, `rgba(${LIME}, ${0.95 * plant})`, 1.6);
      ctx.setLineDash([]);
      ctx.lineDashOffset = 0;
    }

    // ── Callouts ─────────────────────────────────────────────────────────
    const finale = span(p, 0.8, 0.9);
    ctx.font = '500 10.5px "Geist Mono Variable", ui-monospace, monospace';
    ctx.textBaseline = "middle";
    const compact = width < 560;
    CALLOUTS.forEach((callout) => {
      const alpha = easeOut(span(p, callout.at, callout.at + 0.06));
      if (alpha <= 0) return;
      const tone = 0.55 + 0.45 * finale;
      const reach = compact ? 0.55 : 1;
      const ax = sx(callout.anchor);
      const ay = sy(callout.anchor);
      const ex = ax + callout.dx * reach * alpha;
      const ey = ay + callout.dy * reach * alpha;
      const text = callout.label.toUpperCase();
      const right = callout.dx > 0;

      ctx.strokeStyle = `rgba(${LIME}, ${0.7 * alpha * tone})`;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(ax, ay);
      ctx.lineTo(ex, ey);
      ctx.lineTo(ex + (right ? 14 : -14), ey);
      ctx.stroke();

      ctx.beginPath();
      ctx.arc(ax, ay, 3 + 5 * finale * (0.5 + 0.5 * Math.sin(clock * 2.4)), 0, TAU);
      ctx.strokeStyle = `rgba(${LIME}, ${0.5 * alpha})`;
      ctx.stroke();

      ctx.fillStyle = `rgba(${BONE}, ${alpha * tone})`;
      ctx.textAlign = right ? "left" : "right";
      ctx.fillText(text, ex + (right ? 20 : -20), ey + 0.5);
    });
  };

  const ref = useCanvas(draw, { animate, maxDpr: 2 });

  return <canvas ref={ref} aria-hidden="true" className={className} />;
}
